/**
 * «Generar con IA»: del encargo del dueño a una PROPUESTA.
 *
 * Los tipos permitidos (`allowedModuleKinds`, `allowedBlockTypes`) los manda
 * este componente, no el servidor: los registros viven en TypeScript, y
 * cablearlos en Rust crearía una segunda fuente de verdad que se rompe la
 * primera vez que alguien registre un plugin nuevo.
 *
 * Nada de lo que salga de aquí toca la base de datos. Sale una propuesta que se
 * revisa en `AiProposalReview`.
 */
import { type Component, createMemo, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Sparkles } from "lucide-solid";
import { defaultRegistry } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { showToast } from "@/components/ui/toast";
import { moduleRegistry } from "@/features/academy/modules/registry";
import { useCourseSettings } from "@/features/academy/hooks/use-course-modules";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useAiAuthoringAvailability, useProposeEdit, useProposeStructure } from "./api";

const blockTypes = defaultRegistry().types();

export const AiGenerateDialog: Component<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Presente ⇒ se propone un DIFF sobre este curso (flag `…​.edit`). */
  courseId?: string;
  onCreated: (proposalId: string) => void;
}> = (props) => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const settings = useCourseSettings(() => props.courseId);
  const availability = useAiAuthoringAvailability(activeOrgId, () => props.courseId);
  const structure = useProposeStructure();
  const edit = useProposeEdit();
  const [prompt, setPrompt] = createSignal("");

  const editing = () => Boolean(props.courseId);
  const pending = () => structure.isPending || edit.isPending;

  const enabledKinds = createMemo(() =>
    moduleRegistry.enabledKinds(settings.data?.enabled_module_kinds),
  );

  /** Motivo por el que no se puede generar ahora, en palabras del usuario. */
  const blockedReason = createMemo(() => {
    const a = availability.data;
    if (!a) {
      return null;
    }
    if (editing() && !a.editAvailable && a.available) {
      return t(msg`Editing courses with AI is turned off in your academy.`);
    }
    switch (a.reason) {
      case "feature_off": {
        return t(msg`AI authoring is turned off in your academy.`);
      }
      case "unconfigured": {
        return t(msg`Your academy has not configured an AI provider yet.`);
      }
      case "over_quota": {
        return t(msg`This month's AI budget for authoring is used up.`);
      }
      case "rate_limited": {
        return t(msg`Too many requests in a row. Wait a moment.`);
      }
      default: {
        return null;
      }
    }
  });

  const submit = async () => {
    const text = prompt().trim();
    if (!text) {
      return;
    }
    try {
      const input = {
        org: activeOrgId(),
        prompt: text,
        allowedModuleKinds: enabledKinds(),
        allowedBlockTypes: blockTypes,
        courseId: props.courseId,
      };
      const created = editing()
        ? await edit.mutateAsync(input)
        : await structure.mutateAsync(input);
      setPrompt("");
      props.onOpenChange(false);
      props.onCreated(created.proposalId);
      const pieces = created.itemCount;
      showToast(
        t(msg`Proposal ready: ${pieces} pieces to review. Nothing has changed yet.`),
        "success",
      );
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : t(msg`Could not generate the proposal`),
        "error",
      );
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-lg p-0">
      <DialogHeader size="md">
        <DialogTitle class="flex items-center gap-2 text-xl font-bold">
          <Sparkles class="h-5 w-5 text-accent" />
          {editing() ? t(msg`Suggest changes with AI`) : t(msg`Generate a course with AI`)}
        </DialogTitle>
      </DialogHeader>
      <div class="space-y-3 p-6" data-testid="ai-generate-dialog">
        <p class="text-sm text-muted-foreground">
          {editing()
            ? t(
                msg`Describe what you want to improve. The AI proposes changes piece by piece; nothing is applied until you approve it.`,
              )
            : t(
                msg`Describe the training, for example: "Digital marketing, level 1, 3 months, 2 classes per week". The AI writes a proposal; nothing is saved until you approve it.`,
              )}
        </p>
        <textarea
          class="w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 focus-visible:border-primary focus-visible:outline-none"
          rows={4}
          value={prompt()}
          onInput={(e) => {
            setPrompt(e.currentTarget.value);
          }}
          placeholder={t(msg`Marketing digital, nivel 1, 3 meses, 2 clases por semana`)}
          data-testid="ai-generate-prompt"
        />
        <Show when={blockedReason()}>
          {(reason) => (
            <p
              class="rounded-lg bg-muted/50 p-2 text-xs text-muted-foreground"
              data-testid="ai-generate-blocked"
            >
              {reason()}
            </p>
          )}
        </Show>
        <Show when={availability.data && !availability.data.schemaEnforced}>
          <p class="text-xs text-muted-foreground">
            {t(
              msg`Your AI provider does not guarantee the output format, so every piece will need a careful read before approving.`,
            )}
          </p>
        </Show>
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              props.onOpenChange(false);
            }}
          >
            {t(msg`Cancel`)}
          </Button>
          <Button
            disabled={!prompt().trim() || pending() || Boolean(blockedReason())}
            onClick={() => void submit()}
            data-testid="ai-generate-submit"
          >
            <Sparkles class="mr-1 h-4 w-4" />
            {pending() ? t(msg`Generating…`) : t(msg`Generate proposal`)}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
